import React, { FC, useState, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { clone, uniqueId } from 'lodash'
import { useAlert } from 'react-alert'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'
import DateModule from '../../../components/task/MonthCalendarModule'
import { dateManipulateDay, endOfMonth, format, nowDate, secToHour, secToMinute, secToSec, startOfMonth } from '../../../../utils/date'
import { Link, useHistory } from 'react-router-dom'
import {
  createTaskApprovalApplication,
  fetchTaskApprovalApplicationDetail,
  fetchTaskApprovalApplicationList,
} from '../../../../states/containers/task/taskSlice'
import Holidays from 'date-holidays'
import { getGetPrams, setPath, setUrlId } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import ApprovedModal from '../../../components/task/ApprovedModal'
import { labelColor, statusText } from '../../../../utils/taskStatusLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import VideoGuide from '../../../components/common/VideoGuide'
import { TaskApprovalApplicationListMember } from '../../../../states/types/task'
import MultiApprovedModal from '../../../components/task/MultiApprovedModal'
import SvgPieChart from '../../../components/task/SvgPieChart'
import { updateTaskApprovalStatusDisplayRate } from '../../../../states/containers/common/currentSlice'

interface TargetList {
  member_id: number
  member_name: string
  date_list: {
    date: string
    action_needed: boolean
  }[]
}

interface DetailParam {
  member_id: number
  member_name: string
  target_date: string
}

interface UpdateParam {
  member_id: number
  status: string
  comment: string
  target_date: string
}

interface GetParams {
  date: string
}

const Index: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const {
    task,
    current,
    login,
  } = useSelector((state: RootState) => state)

  const holidays = new Holidays()
  holidays.init('JP')

  const [date, setDate] = useState<string>("")
  const [isOpenDetailModal, setIsOpenDetailModal] = useState<boolean>(false)
  const [wait, setWait] = useState<boolean>(false)

  const [memberList, setMemberList] = useState<TaskApprovalApplicationListMember[]>([])
  const [memberListInit, setMemberListInit] = useState<TaskApprovalApplicationListMember[]>([])
  const [selectMemberIds, setSelectMemberIds] = useState<number[]>([])

  const [isOpenMultiApprovalModal, setIsOpenMultiApprovalModal] = useState<boolean>(false)

  const [composition, setComposition] = useState<boolean>(false)
  const [selectDetail, setSelectDetail] = useState<{member_id: number, member_name: string, target_date: string, x: number, y: number}>()
  const [isDisplaySelectDetail, setIsDisplaySelectDetail] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [prevMemberId, setPrevMemberId] = useState<number>(0)

  const [isDisplayRate, setIsDisplayRate] = useState<boolean>(false)

  const [detailData, setDetailData] = useState<DetailParam>({
    member_id: 0,
    member_name: "",
    target_date: "",
  })

  const [displayType, setDisplayType] = useState<string>("all")

  const filteredMemberList = useMemo(() => {
    setSelectMemberIds([])
    if (displayType === "approve") return memberListInit.filter((v) => v.is_approvable)
    if (displayType === "view") return memberListInit.filter((v) => !v.is_approvable)
    return memberListInit
  }, [displayType, memberListInit])

  const targetMonthData = useMemo(() => {
    const dateList = []
    for (let d = clone(new Date(startOfMonth(date || nowDate())));d <= new Date(endOfMonth(date || nowDate()));d.setDate(d.getDate() + 1)) {
      dateList.push(format(d, "YYYY-MM-DD"))
    }
    return Array.from(new Set(dateList))
  }, [date])

  const calendarColor = (day: string, bg = false) => {
    if (Number.isNaN(new Date(day).getTime())) return bg ? "#FFFFFF" : "#666666"
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return bg ? "#fff7f7" : "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return bg ? "#f1f9ff" : "#317AB7"
    }
    return bg ? "#FFFFFF" : "#666666"
  }

  const buildSecToTime = (sec: number) => (
    <>
      {
        !!Number(secToHour(sec)) && (
          <>
            {`${secToHour(sec)}時間`}
          </>
        )
      }
      {
        Number(secToMinute(sec)) ? (
          <>
            {`${secToMinute(sec)}分`}
          </>
        ) : (
          <>
            0分
          </>
        )
      }
      {
        !!Number(secToSec(sec)) && (
          <>
            {`${secToSec(sec)}秒`}
          </>
        )
      }
    </>
  )

  const buildStatusLabel = (status: string, short = false) => {
    let tmpDom = (<></>)
    if (status === "approved") {
      if (short) return (<>承認済み</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'check']} />
          承認済み
        </>
      )
    } else if (status === "completed") {
      if (short) return (<>承認完了</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'check']} />
          承認完了
        </>
      )
    } else if (status === "processing") {
      if (short) return (<>申請中</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['far', 'clock']} />
          申請中
        </>
      )
    } else if (status === "skipped") {
      if (short) return (<>スキップ</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          スキップ
        </>
      )
    } else if (status === "unapplied" || status === "canceled") {
      if (short) return (<>未申請</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          未申請
        </>
      )
    } else if (status === "returned") {
      if (short) return (<>差戻し</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          差戻し
        </>
      )
    } else if (status === "dropped") {
      if (short) return (<>承認取消</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          承認取消
        </>
      )
    }
    return (
      <>
        <div
          className="approved_modal_label"
          style={{
            color: labelColor(status),
            border: `solid 1px ${labelColor(status)}`,
            backgroundColor: labelColor(status, true),
            display: "flex",
            alignItems: "center",
            gap: "4px",
            borderRadius: "4px",
            padding: "2px 4px",
            fontSize: "10px",
          }}
        >
          {tmpDom}
        </div>
      </>
    )
  }

  const close = () => {
    setSelectDetail(undefined)
    setIsDisplaySelectDetail(false)
  }

  const clickDetail = (memberData: DetailParam, isFetch = true) => {
    setDetailData(memberData)
    setIsOpenDetailModal(true)
    close()
    if (isFetch) {
      dispatch(
        fetchTaskApprovalApplicationDetail(
          {
            id: memberData.member_id.toString(),
            target_date: memberData.target_date,
          },
        ),
      )
    }
  }

  const hoverApprovalDetail = (memberId: number, memberName: string, targetDate: string, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const tmpX = event.clientX / document.documentElement.clientWidth < 0.8
      ? event.clientX + (Number(format(targetDate, "DD")) * 1.2)
      : document.documentElement.clientWidth - 240
    setComposition(true)
    setSelectDetail({
      member_id: memberId,
      member_name: memberName,
      target_date: targetDate,
      x: tmpX,
      y: event.clientY - 30,
    })
  }

  const blurApprovalDetail = () => {
    setComposition(false)
  }

  const buildStatus = (memberId: number, targetDate: string) => {
    const memberData = task.taskApprovalApplicationList?.members.find((v) => v.member_id === memberId)
    const detail = memberData?.date_list.find((v) => v.date === targetDate)
    let tmpWord = { text: "", short: "" }
    if (!memberData || !detail || Number(format(targetDate, "YYYYMMDD")) <= 20240430) {
      return (
        <>
          -
        </>
      )
    }
    if (isDisplayRate) {
      tmpWord = { text: `${detail.labeling_rate}%`, short: `${detail.labeling_rate}%` }
    } else if (detail.approval_status === "approved") {
      tmpWord = { text: "承認済み", short: "承" }
    } else if (detail.approval_status === "completed") {
      tmpWord = { text: "承認完了", short: "完" }
    } else if (detail.approval_status === "processing") {
      tmpWord = { text: "申請中", short: "申" }
    } else if (detail.approval_status === "skipped") {
      tmpWord = { text: "スキップ", short: "ス" }
    } else if (detail.approval_status === "unapplied" || detail.approval_status === "canceled") {
      tmpWord = { text: "未申請", short: "未" }
    } else if (detail.approval_status === "returned") {
      tmpWord = { text: "差戻し", short: "差" }
    } else if (detail.approval_status === "dropped") {
      tmpWord = { text: "承認取消", short: "消" }
    }
    return (
      <>
        <div
          className="status_icon"
          style={{
            position: "relative",
            color: labelColor(detail.approval_status),
            border: `solid 1px ${labelColor(detail.approval_status)}`,
            backgroundColor: labelColor(detail.approval_status, true),
            cursor: "pointer",
            zIndex: 2002,
          }}
          onClick={() => clickDetail({
            member_id: memberId,
            member_name: memberData.name,
            target_date: targetDate,
          })}
          onMouseEnter={(e) => hoverApprovalDetail(
            memberId,
            memberData.name,
            targetDate,
            e,
          )}
          onMouseLeave={blurApprovalDetail}
          role="none"
        >
          {
            detail.changed && (
              <FontAwesomeIcon
                icon={['fas', 'exclamation-triangle']}
                style={{ position: "absolute", top: "-6px", left: "-3px", color: "#c1a50d;" }}
              />
            )
          }
          {
            !detail.changed && detail.comment_exists && (
              <FontAwesomeIcon
                icon={['fas', 'comment-dots']}
                flip="horizontal"
                style={{ position: "absolute", top: "-6px", left: "-3px", color: "#999999" }}
              />
            )
          }
          {tmpWord.short}
        </div>
      </>
    )
  }

  const buildStatusLegend = (status: string) => {
    let tmpWord = { text: "", short: "" }
    if (status === "approved") {
      tmpWord = { text: "あなたによってデータが承認されました", short: "承" }
    }
    if (status === "completed") {
      tmpWord = { text: "最終承認者によってデータが承認されました", short: "完" }
    }
    if (status === "processing") {
      tmpWord = { text: "データが申請中です", short: "申" }
    }
    if (status === "unapplied") {
      tmpWord = { text: "データが申請されていません", short: "未" }
    }
    if (status === "returned") {
      tmpWord = { text: "データの申請が差戻しされました", short: "差" }
    }
    if (status === "dropped") {
      tmpWord = { text: "データの承認が取り消されました", short: "消" }
    }
    return (
      <>
        <div className="legend_block">
          <div
            className="status_icon"
            style={{
              color: labelColor(status),
              border: `solid 1px ${labelColor(status)}`,
              backgroundColor: labelColor(status, true),
            }}
          >
            {tmpWord.short}
          </div>
          {`${tmpWord.text}`}
        </div>
      </>
    )
  }

  const updateApproval = (param: UpdateParam, callback: VoidFunction) => {
    const tmpParam = {
      status: param.status,
      targets: [
        {
          target_date: param.target_date,
          member_id: param.member_id,
          comment: param.comment,
        },
      ],
    }
    const isComplete = Boolean(param.status === "approved"
      && task.taskApprovalApplicationDetail?.approvers.filter((v) => v.member_id === login.current_member_id && !v.priority).length)
    const memberName = task.taskApprovalApplicationList?.members.find((v) => v.member_id === param.member_id)?.name || "???"
    dispatch(
      createTaskApprovalApplication(
        tmpParam,
        isComplete,
        () => {
          callback()
          alert.success(`${memberName}さん ${format(param.target_date, "YYYY年M月D日")}の申請データを「${statusText(param.status)}」に変更しました`)
          dispatch(
            fetchTaskApprovalApplicationDetail(
              {
                id: param.member_id.toString(),
                target_date: param.target_date,
              },
            ),
          )
        },
      ),
    )
  }

  const reload = () => {
    setWait(true)
    setSelectMemberIds([])
    dispatch(
      fetchTaskApprovalApplicationList({
        start_date: startOfMonth(date, "YYYY-MM-DD"),
        end_date: endOfMonth(date, "YYYY-MM-DD"),
      }),
    )
    setTimeout(() => {
      setWait(false)
    }, 3000)
  }

  const clickMember = (memberId: number) => {
    if (selectMemberIds.includes(memberId)) {
      setSelectMemberIds((cur) => cur.filter((v) => v !== memberId))
    } else {
      setSelectMemberIds((cur) => [...cur, memberId])
    }
  }

  const clickMemberAll = () => {
    if (selectMemberIds.length === memberList.filter((v) => v.is_approvable).length) {
      setSelectMemberIds([])
    } else {
      setSelectMemberIds(memberList.filter((v) => v.is_approvable).map((v) => v.member_id))
    }
  }

  const updateMultiApproval = (param: {target_list: TargetList[], comment: string}, callback: VoidFunction) => {
    const tmpTargetList = param.target_list.map((v) => v.date_list.map((w) => (
      {
        target_date: w.date,
        member_id: v.member_id || 0,
        comment: null,
      }
    ))).flat()
    const tmpParam = {
      status: "approved",
      comment: param.comment,
      targets: tmpTargetList,
    }
    dispatch(
      createTaskApprovalApplication(
        tmpParam,
        false,
        () => {
          callback()
          alert.success(`チェックした日をまとめて承認しました`)
          dispatch(
            fetchTaskApprovalApplicationList({
              start_date: startOfMonth(date, "YYYY-MM-DD"),
              end_date: endOfMonth(date, "YYYY-MM-DD"),
            }),
          )
        },
      ),
    )
  }

  const buildTargetList = (memberIds: number[]) => {
    const tmp = memberList.filter((v) => memberIds.includes(v.member_id) && v.date_list.filter((w) => w.approval_status === "processing").length)
    return tmp.map((v) => (
      {
        member_id: v.member_id,
        member_name: v.name,
        date_list: v.date_list.filter((w) => (
          w.approval_status === "processing"
        )).map((w) => ({ date: w.date, action_needed: w.action_needed })),
      }
    ))
  }

  const toggleDisplayRate = (displayRate: boolean) => {
    setIsDisplayRate(displayRate)
    dispatch(updateTaskApprovalStatusDisplayRate(displayRate))
  }

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
  }, [])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date, "YYYY-MM-DD"))
      return
    }
    setDate(format(current.targetDate, "YYYY-MM-DD") || nowDate("YYYY-MM-DD"))
  }, [])

  useEffect(() => {
    if (!date) return
    dispatch(
      fetchTaskApprovalApplicationList({
        start_date: startOfMonth(date, "YYYY-MM-DD"),
        end_date: endOfMonth(date, "YYYY-MM-DD"),
      }),
    )
  }, [date])

  useEffect(() => {
    if (!current.taskApprovalStatusDisplayRate) return
    setIsDisplayRate(current.taskApprovalStatusDisplayRate)
  }, [current.taskApprovalStatusDisplayRate])

  useEffect(() => {
    if (!task.taskApprovalApplicationList?.members) return
    setMemberList(task.taskApprovalApplicationList.members || [])
    setMemberListInit(task.taskApprovalApplicationList.members || [])
  }, [task.taskApprovalApplicationList])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (composition && selectDetail) {
        setIsDisplaySelectDetail(true)
        if (
          (format(task.taskApprovalApplicationDetail?.labeling_status.start_time) !== format(selectDetail.target_date))
          || prevMemberId !== selectDetail.member_id
        ) {
          setIsLoading(true)
          dispatch(
            fetchTaskApprovalApplicationDetail(
              {
                target_date: format(selectDetail.target_date, "YYYY-MM-DD"),
                id: selectDetail.member_id.toString(),
              },
              () => {
                setPrevMemberId(selectDetail.member_id)
                setTimeout(() => {
                  setIsLoading(false)
                }, 500)
              },
            ),
          )
        }
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [composition, selectDetail])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 承認状況</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        承認状況
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          承認状況
          <VideoGuide
            videoId="QLw-8P-S__U"
          />
        </h1>
        <div className="tools">
          <DateModule
            date={date}
            startMonth="202405"
            changeCallback={(callback_date) => {
              setDate(callback_date)
              setSelectMemberIds([])
              history.push(`${location.pathname}?date=${callback_date}`)
            }}
          />
        </div>
      </section>
      <section className="page_description">
        <p>
          あなたが承認するユーザーの申請状況を確認することができます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              承認状況
              {`【${format(date, "YYYY年M月")}】`}
              <div className="button_area">
                <button
                  type="button"
                  className="reload"
                  onClick={reload}
                  disabled={wait}
                >
                  <Tips
                    description="最新の情報に更新"
                    className=""
                    icon={false}
                  >
                    <>
                      <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                    </>
                  </Tips>
                </button>
              </div>
              <div
                key={`display_rate_${isDisplayRate}`}
                style={{
                  fontSize: "12px",
                  fontWeight: "normal",
                }}
              >
                {
                  isDisplayRate ? (
                    <>
                      <Tips
                        description="ステータス表示に切り替えます"
                        className=""
                        icon={false}
                      >
                        <>
                          <button
                            type="button"
                            onClick={() => toggleDisplayRate(false)}
                            style={{
                              border: "solid 1px #CCC",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              gap: "4px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "2px",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'percentage']} />
                              仕分け率表示
                            </div>
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <div
                              style={{
                                display: "flex",
                                gap: "2px",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'star']} />
                              ステータス表示
                            </div>
                          </button>
                        </>
                      </Tips>
                    </>
                  ) : (
                    <>
                      <Tips
                        description="仕分け率表示に切り替えます"
                        className=""
                        icon={false}
                      >
                        <>
                          <button
                            type="button"
                            onClick={() => toggleDisplayRate(true)}
                            style={{
                              border: "solid 1px #CCC",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              gap: "4px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "2px",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'star']} />
                              ステータス表示
                            </div>
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <div
                              style={{
                                display: "flex",
                                gap: "2px",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'percentage']} />
                              仕分け率表示
                            </div>
                          </button>
                        </>
                      </Tips>
                    </>
                  )
                }
              </div>
            </div>
            <div className="application_legend">
              <div className="legend_row">
                {buildStatusLegend("unapplied")}
                {buildStatusLegend("returned")}
                {buildStatusLegend("dropped")}
              </div>
              <div className="legend_row">
                {buildStatusLegend("processing")}
                {buildStatusLegend("approved")}
                {buildStatusLegend("completed")}
              </div>
              <div
                style={{
                  borderBottom: "dashed 2px #CCCCCC",
                }}
              />
              <div className="legend_row">
                <div className="legend_block">
                  <FontAwesomeIcon
                    icon={['fas', 'exclamation-triangle']}
                    style={{ color: "#c1a50d" }}
                  />
                  <FontAwesomeIcon
                    icon={['fas', 'exclamation-triangle']}
                    style={{ color: "#6c8ebf" }}
                  />
                  時間変更データあり
                </div>
                <div className="legend_block">
                  <FontAwesomeIcon
                    icon={['fas', 'comment-dots']}
                    flip="horizontal"
                    style={{ color: "#999999" }}
                  />
                  申請コメントあり
                </div>
              </div>
            </div>
          </div>
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    承認情報を取得しています…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    !memberList.length ? (
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#999999",
                          margin: "16px",
                        }}
                      >
                        表示できるデータがありません
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            border: `solid 1px ${selectMemberIds.length ? "#228b22" : "#999999"}`,
                            borderRadius: "4px",
                            backgroundColor: selectMemberIds.length ? "#f3fff9" : "#EEEEEE",
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "right",
                            gap: "12px",
                            marginBottom: "16px",
                            position: "sticky",
                            top: "60px",
                            zIndex: "2003",
                          }}
                        >
                          {
                            selectMemberIds.length ? (
                              <>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: "2px",
                                  }}
                                >
                                  <div
                                    style={{
                                      color: "#228b22",
                                      fontSize: "20px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {selectMemberIds.length}
                                  </div>
                                  <div>
                                    名のユーザーが選択されています
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setIsOpenMultiApprovalModal(true)}
                                  style={{
                                    border: "1px solid #228b22",
                                    borderRadius: "4px",
                                    backgroundColor: "#228b22",
                                    color: "#FFFFFF",
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={["fas", "check"]}
                                    fixedWidth
                                  />
                                  まとめて承認する
                                </button>
                              </>
                            ) : (
                              <>
                                <div
                                  style={{
                                    marginTop: "8px",
                                  }}
                                >
                                  ユーザーが選択されていません
                                </div>
                                <button
                                  type="button"
                                  disabled
                                  style={{
                                    border: "1px solid #DDDDDD",
                                    borderRadius: "4px",
                                    backgroundColor: "#DDDDDD",
                                    color: "#999999",
                                    display: "flex",
                                    gap: "4px",
                                    cursor: "not-allowed",
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={["fas", "check"]}
                                    fixedWidth
                                  />
                                  まとめて承認する
                                </button>
                              </>
                            )
                          }
                        </div>
                        <div
                          style={{
                            position: "sticky",
                            top: "108px",
                            backgroundColor: "#FFFFFF",
                            zIndex: "2003",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            borderBottom: "solid 1px #CCCCCC",
                            marginBottom: "16px",
                            padding: "0 8px",
                          }}
                        >
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderBottom: displayType === "all" ? "solid 2px #395fd5" : "none",
                              borderRadius: "4px 4px 0 0",
                              marginBottom: displayType === "all" ? "-1px" : "-4px",
                              padding: displayType === "all" ? "8px 12px" : "6px 12px",
                              cursor: "pointer",
                            }}
                            onClick={() => setDisplayType("all")}
                            role="none"
                          >
                            すべてのユーザー
                          </div>
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderBottom: displayType === "approve" ? "solid 2px #395fd5" : "none",
                              borderRadius: "4px 4px 0 0",
                              marginBottom: displayType === "approve" ? "-1px" : "-4px",
                              padding: displayType === "approve" ? "8px 12px" : "6px 12px",
                              cursor: "pointer",
                            }}
                            onClick={() => setDisplayType("approve")}
                            role="none"
                          >
                            承認対象ユーザー
                          </div>
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderBottom: displayType === "view" ? "solid 2px #395fd5" : "none",
                              borderRadius: "4px 4px 0 0",
                              marginBottom: displayType === "view" ? "-1px" : "-4px",
                              padding: displayType === "view" ? "8px 12px" : "6px 12px",
                              cursor: "pointer",
                            }}
                            onClick={() => setDisplayType("view")}
                            role="none"
                          >
                            閲覧対象ユーザー
                          </div>
                        </div>
                        <div
                          className="calendar_table_row header_area"
                          style={{
                            position: "sticky",
                            top: "140px",
                            backgroundColor: "#FFFFFF",
                            zIndex: "2003",
                          }}
                        >
                          {
                            displayType !== "view" && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "calc(50% - 5px)",
                                  left: "-20px",
                                  transform: "translateY(-50%)",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  backgroundColor: "#FFFFFF",
                                }}
                                onClick={() => clickMemberAll()}
                                role="none"
                              >
                                <div
                                  style={{
                                    fontSize: "8px",
                                    color: "#999999",
                                    transform: "translateX(-25%)",
                                    userSelect: "none",
                                  }}
                                >
                                  全選択
                                </div>
                                <FontAwesomeIcon
                                  icon={[
                                    'far',
                                    selectMemberIds.length === memberList.filter((v) => v.is_approvable).length ? 'check-square' : 'square',
                                  ]}
                                  style={{
                                    backgroundColor: "#ffffff",
                                    color: selectMemberIds.length === memberList.filter((v) => v.is_approvable).length ? "#0f9655" : "#666666",
                                  }}
                                />
                              </div>
                            )
                          }
                          <div className="left_area">
                            ユーザー名 / 月間仕分け率
                          </div>
                          <div className="right_area">
                            {
                              targetMonthData.map((v) => (
                                <div
                                  key={`list_${v}`}
                                  style={{
                                    color: calendarColor(v),
                                    width: `calc(100% / ${targetMonthData.length})`,
                                    minWidth: "20px",
                                  }}
                                >
                                  {format(v, "D")}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                        {
                          filteredMemberList.map((member) => (
                            <div
                              key={`row_${member.member_id}`}
                              className="calendar_table_row"
                            >
                              {
                                member.is_approvable && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      left: "-20px",
                                      transform: "translateY(-50%)",
                                      fontSize: "14px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => clickMember(member.member_id)}
                                    role="none"
                                  >
                                    <FontAwesomeIcon
                                      icon={['far', selectMemberIds.includes(member.member_id) ? 'check-square' : 'square']}
                                      style={{
                                        backgroundColor: "#ffffff",
                                        color: selectMemberIds.includes(member.member_id) ? "#0f9655" : "#666666",
                                      }}
                                    />
                                  </div>
                                )
                              }
                              <div
                                className="left_area"
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                                onClick={() => clickMember(member.member_id)}
                                role="none"
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "2px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <img
                                      alt={member.name}
                                      src={member.profile_image ?? `/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                      style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                                    />
                                    <Link
                                      to={`${setPath(setUrlId(URL.TASK_APPROVAL_STATUS_DETAIL.path, 'id', member.member_id), "ja")}?date=${date}`}
                                      style={{ textDecoration: "underline" }}
                                    >
                                      <ruby>
                                        {member.name}
                                        <rt>{member.kana}</rt>
                                      </ruby>
                                    </Link>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "10px",
                                      color: "#999999",
                                    }}
                                  >
                                    {member.code ? ` (${member.code})` : ""}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    color: "#109554",
                                  }}
                                >
                                  {`${member.labeling_rate || 0}%`}
                                </div>
                              </div>
                              <div className="right_area">
                                {
                                  targetMonthData.map((v) => (
                                    <div
                                      key={uniqueId("member_column_")}
                                      style={{
                                        width: `calc(100% / ${targetMonthData.length})`,
                                        minWidth: "20px",
                                        // eslint-disable-next-line max-len
                                        backgroundColor: Number(format(v, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD")) ? calendarColor(v, true) : "#EEEEEE",
                                      }}
                                    >
                                      {buildStatus(member.member_id, v)}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          ))
                        }
                      </>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      {
        isDisplaySelectDetail
        && selectDetail
        && task.taskApprovalApplicationDetail
        && format(task.taskApprovalApplicationDetail.labeling_status.start_time) === format(selectDetail.target_date)
        && prevMemberId === selectDetail.member_id
        && (
          <>
            <div
              style={{
                position: "fixed",
                top: selectDetail.y,
                left: selectDetail.x,
                border: "solid 1px #999999",
                borderRadius: "4px",
                backgroundColor: "#FFFFFF",
                padding: "16px",
                transform: "translate(-40%, calc(-100% + 20px))",
                zIndex: 2005,
              }}
            >
              {
                isLoading ? (
                  <>
                    <LoadingSquare>
                      <>
                        詳細情報を取得しています…
                      </>
                    </LoadingSquare>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          right: 0,
                          color: "#999999",
                        }}
                        onClick={close}
                        role="none"
                      >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        <div>
                          {selectDetail.member_name}
                        </div>
                        <div>
                          {format(selectDetail.target_date, "YYYY年MM月DD日")}
                        </div>
                        <div>
                          {buildStatusLabel(task.taskApprovalApplicationDetail.approval_status)}
                        </div>
                      </div>
                      {
                        task.taskApprovalApplicationDetail.labeling_status.changed && (
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "2px",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              backgroundColor: "#f5e41b",
                            }}
                          >
                            <div>
                              勤務開始・終了時刻の変更に関係する作業データ、
                            </div>
                            <div>
                              または、業務外指定範囲設定があります。
                            </div>
                          </div>
                        )
                      }
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "0 40px",
                          }}
                        >
                          <SvgPieChart
                            list={[
                              {
                                label: "仕分け済み",
                                value: task.taskApprovalApplicationDetail.labeling_status.labeling || 0,
                                color: "#0D9554",
                              },
                              {
                                label: "未仕分け",
                                value: task.taskApprovalApplicationDetail.labeling_status.not_labeling || 0,
                                color: "#c61616",
                              },
                            ]}
                            graphTitle=""
                            chartSize={100}
                            displayOuterLabel
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "4px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 4px 4px",
                              borderBottom: "solid 1px #999999",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#ffffff",
                                backgroundColor: "#c921f3",
                                borderRadius: "4px",
                                padding: "2px 4px",
                                fontSize: "10px",
                              }}
                            >
                              勤務時間
                            </div>
                            <div>
                              {format(task.taskApprovalApplicationDetail.labeling_status.start_time, "HH:mm:ss")}
                              ~
                              {format(task.taskApprovalApplicationDetail.labeling_status.end_time, "HH:mm:ss")}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 4px 4px",
                              borderBottom: "solid 1px #999999",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#ffffff",
                                backgroundColor: "#2196f3",
                                borderRadius: "4px",
                                padding: "2px 4px",
                                fontSize: "10px",
                              }}
                            >
                              実労働時間
                            </div>
                            <div>
                              {buildSecToTime(task.taskApprovalApplicationDetail.labeling_status.total_work_time || 0)}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 4px 4px",
                              borderBottom: "solid 1px #999999",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#ffffff",
                                backgroundColor: "#e88042",
                                borderRadius: "4px",
                                padding: "2px 4px",
                                fontSize: "10px",
                              }}
                            >
                              休憩時間
                            </div>
                            <div>
                              {buildSecToTime(task.taskApprovalApplicationDetail.labeling_status.break_time || 0)}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 4px 4px",
                              borderBottom: "solid 1px #999999",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#ffffff",
                                backgroundColor: "#7d0000",
                                borderRadius: "4px",
                                padding: "2px 4px",
                                fontSize: "10px",
                              }}
                            >
                              残業時間
                            </div>
                            <div>
                              {buildSecToTime(task.taskApprovalApplicationDetail.labeling_status.over_time || 0)}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 4px 4px",
                              borderBottom: "solid 1px #999999",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#ffffff",
                                backgroundColor: "#0c9554",
                                borderRadius: "4px",
                                padding: "2px 4px",
                                fontSize: "10px",
                              }}
                            >
                              仕分け済み時間
                            </div>
                            <div>
                              {buildSecToTime(task.taskApprovalApplicationDetail.labeling_status.labeling || 0)}
                              {/* eslint-disable-next-line max-len */}
                              {`(${Math.round((task.taskApprovalApplicationDetail.labeling_status.labeling / (task.taskApprovalApplicationDetail.labeling_status.labeling + task.taskApprovalApplicationDetail.labeling_status.not_labeling)) * 1000) / 10}%)`}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 4px 4px",
                              borderBottom: "solid 1px #999999",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#ffffff",
                                backgroundColor: "#c61616",
                                borderRadius: "4px",
                                padding: "2px 4px",
                                fontSize: "10px",
                              }}
                            >
                              未仕分け時間
                            </div>
                            <div>
                              {buildSecToTime(task.taskApprovalApplicationDetail.labeling_status.not_labeling || 0)}
                              {/* eslint-disable-next-line max-len */}
                              {`(${Math.round((task.taskApprovalApplicationDetail.labeling_status.not_labeling / (task.taskApprovalApplicationDetail.labeling_status.labeling + task.taskApprovalApplicationDetail.labeling_status.not_labeling)) * 1000) / 10}%)`}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              padding: "0 4px 4px",
                              borderBottom: "solid 1px #999999",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#ffffff",
                                backgroundColor: "#4A4A4A",
                                borderRadius: "4px",
                                padding: "2px 4px",
                                fontSize: "10px",
                              }}
                            >
                              業務時間外範囲の合計時間
                            </div>
                            <div>
                              {buildSecToTime(task.taskApprovalApplicationDetail.labeling_status.not_working_time || 0)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            border: "solid 1px #999999",
                            borderRadius: "4px",
                            backgroundColor: "#eeeeee",
                            fontSize: "10px",
                          }}
                          onClick={() => clickDetail({
                            member_id: selectDetail.member_id,
                            member_name: selectDetail.member_name,
                            target_date: selectDetail.target_date,
                          }, false)}
                        >
                          もっと詳しく見る
                        </button>
                      </div>
                    </div>
                  </>
                )
              }
            </div>
            <div className="close_screen" role="presentation" style={{ zIndex: 2001 }} onClick={close} />
          </>
        )
      }
      <ApprovedModal
        detail={detailData}
        closeCallback={() => setIsOpenDetailModal(false)}
        updateCallback={(param, callback) => updateApproval(param, callback)}
        isDisplay={isOpenDetailModal}
        moveDateCallback={(addNum) => clickDetail({ ...detailData, target_date: dateManipulateDay(detailData.target_date, addNum, "YYYY-MM-DD") })}
        isDisplayWorkTime
      />
      <MultiApprovedModal
        targetList={buildTargetList(selectMemberIds)}
        closeCallback={() => setIsOpenMultiApprovalModal(false)}
        updateCallback={(param, callback) => updateMultiApproval(param, callback)}
        isDisplay={isOpenMultiApprovalModal}
      />
    </section>
  )
}

export default Index
